import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useSkipsData } from '../context/SkipsDataContext';
import { useResponsive } from '../hooks/useResponsive';
import { SKIP_SIZES } from '../constants/jobOptions';
import ViewToggle from './ViewToggle';
import toast from 'react-hot-toast';

const Inventory = () => {
  const { companyId, userIsAdmin, jobs } = useSkipsData();
  const viewport = useResponsive();

  // Firestore state
  const [inventoryEnabled, setInventoryEnabled] = useState(false);
  const [inventory, setInventory] = useState({});
  const [loading, setLoading] = useState(true);

  // Edit state (local draft before saving)
  const [editInventory, setEditInventory] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Add-size form state
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');

  // Load company inventory data
  const loadCompanyData = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      const companyDocRef = doc(db, 'companies', companyId);
      const companyDoc = await getDoc(companyDocRef);

      if (companyDoc.exists()) {
        const data = companyDoc.data();
        const enabled = data.inventoryEnabled || false;
        const inv = data.inventory || {};

        setInventoryEnabled(enabled);
        setInventory(inv);
        setEditInventory(inv);
      }
    } catch (error) {
      console.error('Error loading inventory data:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  // Compute "out" counts from live job data
  const outCounts = useMemo(() => {
    const counts = {};
    jobs.forEach(job => {
      if (job.status === 'delivered' && job.size) {
        counts[job.size] = (counts[job.size] || 0) + 1;
      }
    });
    return counts;
  }, [jobs]);

  // Toggle inventory ON/OFF
  const handleToggle = async () => {
    if (!userIsAdmin) {
      toast.error('Only admins can toggle inventory tracking');
      return;
    }

    const newValue = !inventoryEnabled;

    try {
      const companyDocRef = doc(db, 'companies', companyId);
      await updateDoc(companyDocRef, { inventoryEnabled: newValue });
      setInventoryEnabled(newValue);
      toast.success(newValue ? 'Inventory tracking enabled' : 'Inventory tracking disabled');
    } catch (error) {
      console.error('Error toggling inventory:', error);
      toast.error('Failed to update setting');
    }
  };

  // Add a new skip size to the inventory
  const handleAddSize = () => {
    if (!selectedSize) {
      toast.error('Please select a skip size');
      return;
    }

    const qty = parseInt(selectedQuantity, 10);
    if (isNaN(qty) || qty < 0) {
      toast.error('Please enter a valid quantity (0 or more)');
      return;
    }

    if (editInventory[selectedSize] !== undefined) {
      toast.error(`${selectedSize} is already in your inventory. Edit the quantity in the table below.`);
      return;
    }

    const updated = { ...editInventory, [selectedSize]: qty };
    setEditInventory(updated);
    setHasUnsavedChanges(true);
    setSelectedSize('');
    setSelectedQuantity('');
  };

  // Edit quantity inline in the table
  const handleQuantityChange = (size, value) => {
    const qty = value === '' ? '' : parseInt(value, 10);
    setEditInventory(prev => ({ ...prev, [size]: qty }));
    setHasUnsavedChanges(true);
  };

  // Remove a size from the inventory
  const handleRemoveSize = (size) => {
    const updated = { ...editInventory };
    delete updated[size];
    setEditInventory(updated);
    setHasUnsavedChanges(true);
  };

  // Save inventory to Firestore
  const handleSave = async () => {
    for (const [size, qty] of Object.entries(editInventory)) {
      const parsed = parseInt(qty, 10);
      if (isNaN(parsed) || parsed < 0) {
        toast.error(`Invalid quantity for ${size}. Must be 0 or more.`);
        return;
      }
    }

    const normalized = {};
    for (const [size, qty] of Object.entries(editInventory)) {
      normalized[size] = parseInt(qty, 10);
    }

    try {
      const companyDocRef = doc(db, 'companies', companyId);
      await updateDoc(companyDocRef, { inventory: normalized });
      setInventory(normalized);
      setEditInventory(normalized);
      setHasUnsavedChanges(false);
      toast.success('Inventory saved successfully');
    } catch (error) {
      console.error('Error saving inventory:', error);
      toast.error('Failed to save inventory');
    }
  };

  // Discard unsaved changes
  const handleDiscard = () => {
    setEditInventory(inventory);
    setHasUnsavedChanges(false);
  };

  // Sort inventory entries in SKIP_SIZES order
  const sortedSizes = useMemo(() => {
    const sizeOrder = SKIP_SIZES.reduce((acc, size, idx) => {
      acc[size] = idx;
      return acc;
    }, {});

    return Object.entries(editInventory)
      .sort(([a], [b]) => (sizeOrder[a] ?? 999) - (sizeOrder[b] ?? 999));
  }, [editInventory]);

  // Sizes not yet added to inventory
  const availableSizesToAdd = useMemo(() => {
    return SKIP_SIZES.filter(size => editInventory[size] === undefined);
  }, [editInventory]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#FAFAF9',
      paddingTop: '50px'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: viewport.isMobile ? '1rem' : '2rem'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#292524',
      margin: 0,
      letterSpacing: '-0.025em'
    },
    subtitle: {
      fontSize: '14px',
      color: '#78716C',
      marginTop: '0.25rem'
    },
    card: {
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #E7E5E4',
      boxShadow: '0 1px 3px rgba(28, 25, 23, 0.06)',
      marginBottom: '1.5rem'
    },
    label: {
      display: 'block',
      fontSize: '12px',
      fontWeight: '500',
      color: '#78716C',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.025em'
    },
    input: {
      padding: '8px 12px',
      fontSize: '14px',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      boxSizing: 'border-box',
      color: '#292524',
      transition: 'border-color 150ms ease, box-shadow 150ms ease'
    },
    primaryButton: {
      padding: '8px 20px',
      backgroundColor: '#166534',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 150ms ease'
    },
    secondaryButton: {
      padding: '8px 20px',
      backgroundColor: 'white',
      color: '#44403C',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem',
      color: '#78716C',
      fontSize: '1rem'
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    toggleTrack: {
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      backgroundColor: inventoryEnabled ? '#166534' : '#D6D3D1',
      position: 'relative',
      cursor: userIsAdmin ? 'pointer' : 'default',
      transition: 'background-color 200ms ease',
      opacity: userIsAdmin ? 1 : 0.6,
      flexShrink: 0
    },
    toggleThumb: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: 'white',
      position: 'absolute',
      top: '2px',
      left: inventoryEnabled ? '22px' : '2px',
      transition: 'left 200ms ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      fontSize: '12px',
      fontWeight: '500',
      color: '#78716C',
      textTransform: 'uppercase',
      letterSpacing: '0.025em',
      borderBottom: '2px solid #E7E5E4',
      backgroundColor: '#FAFAF9'
    },
    td: {
      padding: '12px 16px',
      fontSize: '14px',
      color: '#292524',
      borderBottom: '1px solid #E7E5E4'
    },
    lowStockBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 8px',
      backgroundColor: '#FEF2F2',
      color: '#DC2626',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      marginLeft: '8px'
    },
    removeButton: {
      padding: '4px 10px',
      backgroundColor: '#FEE2E2',
      color: '#991B1B',
      border: '1px solid #FECACA',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer'
    }
  };

  if (loading) {
    return (
      <>
        <ViewToggle />
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.emptyState}>Loading inventory...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ViewToggle />
      <div style={styles.container}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div>
              <h1 style={styles.title}>Inventory</h1>
              <div style={styles.subtitle}>
                Track your skip fleet by size
              </div>
            </div>
          </div>

          {/* ON/OFF Toggle Card */}
          <div style={styles.card}>
            <div style={styles.toggleContainer}>
              <div
                style={styles.toggleTrack}
                onClick={userIsAdmin ? handleToggle : undefined}
              >
                <div style={styles.toggleThumb} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#292524' }}>
                  Inventory Tracking
                </div>
                <div style={{ fontSize: '13px', color: '#78716C' }}>
                  {inventoryEnabled ? 'Enabled' : 'Disabled'}
                  {!userIsAdmin && ' (Contact an admin to change)'}
                </div>
              </div>
            </div>
          </div>

          {/* Feature OFF state */}
          {!inventoryEnabled && (
            <div style={styles.card}>
              <div style={styles.emptyState}>
                <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#292524', marginBottom: '0.5rem' }}>
                  Inventory tracking is currently disabled
                </div>
                <div style={{ maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>
                  Enable inventory tracking to monitor your skip fleet.
                  Set the total number of each skip size you own, and SkipEye will
                  automatically calculate how many are currently out on site and how
                  many are available.
                </div>
                {!userIsAdmin && (
                  <div style={{ marginTop: '1rem', fontSize: '14px', color: '#57534E' }}>
                    Contact your admin to enable this feature.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Feature ON state */}
          {inventoryEnabled && (
            <>
              {/* Add Size Form (admin only) */}
              {userIsAdmin && (
                <div style={styles.card}>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#292524', marginBottom: '1rem' }}>
                    Add Skip Size
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-end',
                    flexWrap: viewport.isMobile ? 'wrap' : 'nowrap'
                  }}>
                    <div style={{ flex: viewport.isMobile ? '1 1 100%' : '1' }}>
                      <label style={styles.label}>Skip Size</label>
                      <select
                        value={selectedSize}
                        onChange={(e) => setSelectedSize(e.target.value)}
                        style={{ ...styles.input, width: '100%' }}
                      >
                        <option value="">Select size...</option>
                        {availableSizesToAdd.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: viewport.isMobile ? '1 1 100%' : '0 0 120px' }}>
                      <label style={styles.label}>Total Owned</label>
                      <input
                        type="number"
                        min="0"
                        value={selectedQuantity}
                        onChange={(e) => setSelectedQuantity(e.target.value)}
                        placeholder="0"
                        style={{ ...styles.input, width: '100%' }}
                      />
                    </div>
                    <button
                      onClick={handleAddSize}
                      style={{ ...styles.primaryButton, whiteSpace: 'nowrap' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#14532D'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#166534'}
                    >
                      + Add
                    </button>
                  </div>
                </div>
              )}

              {/* Summary Table */}
              {sortedSizes.length === 0 ? (
                <div style={styles.card}>
                  <div style={styles.emptyState}>
                    {userIsAdmin
                      ? 'No skip sizes added yet. Use the form above to add your first skip size.'
                      : 'No inventory data available. Contact your admin to set up inventory tracking.'}
                  </div>
                </div>
              ) : (
                <div style={styles.card}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Size</th>
                          <th style={styles.th}>Total</th>
                          <th style={styles.th}>Out</th>
                          <th style={styles.th}>Available</th>
                          {userIsAdmin && <th style={{ ...styles.th, width: '100px' }}>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSizes.map(([size, total]) => {
                          const totalNum = parseInt(total, 10) || 0;
                          const out = outCounts[size] || 0;
                          const available = totalNum - out;

                          return (
                            <tr key={size}>
                              <td style={{ ...styles.td, fontWeight: '500' }}>{size}</td>
                              <td style={styles.td}>
                                {userIsAdmin ? (
                                  <input
                                    type="number"
                                    min="0"
                                    value={editInventory[size] ?? ''}
                                    onChange={(e) => handleQuantityChange(size, e.target.value)}
                                    style={{ ...styles.input, width: '80px' }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#166534';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#D6D3D1';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                ) : (
                                  totalNum
                                )}
                              </td>
                              <td style={styles.td}>{out}</td>
                              <td style={styles.td}>
                                <span>{available}</span>
                                {available === 1 && totalNum > 0 && (
                                  <span style={styles.lowStockBadge}>Low stock</span>
                                )}
                                {available === 0 && totalNum > 0 && (
                                  <span style={styles.lowStockBadge}>None available</span>
                                )}
                                {available < 0 && (
                                  <span style={styles.lowStockBadge}>
                                    Over-deployed by {Math.abs(available)}
                                  </span>
                                )}
                              </td>
                              {userIsAdmin && (
                                <td style={styles.td}>
                                  <button
                                    onClick={() => handleRemoveSize(size)}
                                    style={styles.removeButton}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#FECACA';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = '#FEE2E2';
                                    }}
                                  >
                                    Remove
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Save / Discard buttons (admin only, when changes exist) */}
                  {userIsAdmin && hasUnsavedChanges && (
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginTop: '1.5rem',
                      paddingTop: '1.5rem',
                      borderTop: '1px solid #E7E5E4'
                    }}>
                      <button
                        onClick={handleSave}
                        style={styles.primaryButton}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#14532D'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#166534'}
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={handleDiscard}
                        style={styles.secondaryButton}
                      >
                        Discard
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Non-admin info message */}
              {!userIsAdmin && sortedSizes.length > 0 && (
                <div style={{
                  backgroundColor: '#F5F5F4',
                  border: '1px solid #E7E5E4',
                  borderRadius: '6px',
                  padding: '1rem',
                  fontSize: '14px',
                  color: '#57534E',
                  textAlign: 'center'
                }}>
                  Only admins can edit inventory quantities. Contact your admin to make changes.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Inventory;
